import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import useProyecto from "../../hooks/useProyecto";
import { formatMonto, getValue } from "../../utils";
import { PagosContext } from "../../context/PagosContext";
import { EstimacionesContext } from "../../context/EstimacionesContext";
import { ContratosContext } from "../../context/ContratosContext";
import ReactSwitch from "react-switch";
import { MontosContext } from "../../context/MontosContext";
import { getAnticipo } from "../../utils/pagos";
import MontosSugeridosEstimacion from "../montos/MontosSugeridosEstimacion";
import MontosSugeridosAnticipo from "../montos/MontosSugeridosAnticipo";
import usePreciario from "../../hooks/usePreciario";

const PagoForm = ({ idProyecto, idContrato, handleCancel, handleCallback }) => {
  const { pago, pagos, postPagoContrato, setPropiedadPago } =
    useContext(PagosContext);
  const { montos, getMontosContrato } = useContext(MontosContext);
  const { contrato } = useContext(ContratosContext);
  const { estimaciones, getEstimacionesContrato } =
    useContext(EstimacionesContext);
  const { permiso } = useProyecto();

  const { porAmortizar } = usePreciario();

  const [errorAmortizado, setErrorAmortizado] = useState(null);
  const [idEstimacion, setIdEstimacion] = useState(null);
  const [isAnticipo, setIsAnticipo] = useState(false);
  const [sugerencia, setSugerencia] = useState(null);

  useEffect(() => {
    return () => {
      setSugerencia(null);
      setIsAnticipo(false);
    };
  }, []);

  useEffect(() => {
    fetchEstimaciones();
    getMontosContrato(idProyecto, idContrato);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idContrato]);

  useEffect(() => {
    if (
      pago !== null &&
      contrato !== null &&
      Array.isArray(pagos) &&
      montos &&
      montos !== null
    ) {
      if (isAnticipo) handleAnticipo();
      else handleSugerencia();
    }
    if (idEstimacion !== null) {
      const currentEstimacion = estimaciones?.find(
        (estimacion) =>
          parseInt(idEstimacion) === parseInt(estimacion.idEstimacion)
      );
      if (currentEstimacion)
        setPropiedadPago(
          "comentario",
          `Estimación #${currentEstimacion.numero}`
        );
    } else if (!getValue(pago, "comentario")) {
      setPropiedadPago("comentario", null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEstimacion, isAnticipo, contrato, montos, pagos]);

  useEffect(() => {
    if (pago && pago !== null) {
      if (parseFloat(pago.monto_amortizado) > porAmortizar) {
        setErrorAmortizado(true);
      } else setErrorAmortizado(false);
    }
  }, [pago, porAmortizar]);

  function handleAnticipo() {
    if (contrato.anticipo !== null && sugerencia === null) {
      const montoSugerido =
        montos.autorizado * (parseFloat(contrato.anticipo) / 100);
      setPropiedadPago("monto", montoSugerido);
      setSugerencia(
        <MontosSugeridosAnticipo
          sugerido={montoSugerido}
          anticipo={contrato.anticipo}
          autorizado={montos.autorizado}
        />
      );
    }
  }

  function handleSugerencia() {
    if (idEstimacion === null) return setSugerencia(null);
    const currentEstimacion = estimaciones?.find(
      (estimacion) =>
        parseInt(idEstimacion) === parseInt(estimacion.idEstimacion)
    );
    if (currentEstimacion && Array.isArray(pagos)) {
      const { anticipo } = contrato;
      const montoAnticipo = getAnticipo(pagos);
      const { estimado, autorizado } = currentEstimacion;
      const montoSugerido = autorizado * (1 - parseFloat(anticipo) / 100);
      setPropiedadPago("monto", montoSugerido.toFixed(2));
      setPropiedadPago(
        "monto_amortizado",
        (autorizado - montoSugerido).toFixed(2)
      );
      setSugerencia(
        <MontosSugeridosEstimacion
          estimado={estimado}
          anticipo={anticipo}
          autorizado={autorizado}
          sugerido={montoSugerido}
          contrato={montos.autorizado}
          montoAnticipo={montoAnticipo}
        />
      );
    }
  }

  function fetchEstimaciones() {
    getEstimacionesContrato(idProyecto, idContrato);
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const pagoData = { ...pago, anticipo: isAnticipo, idEstimacion };
    postPagoContrato(idProyecto, idContrato, pagoData, handleCallback);
  };

  const renderAnticipo = () => {
    if (contrato.anticipo === null) {
      return (
        <p className="text-danger">
          Aún no has configurado el anticipo del contrato.
        </p>
      );
    }
    if (
      Array.isArray(pagos) &&
      pagos.length === 0 &&
      contrato.anticipo !== null
    ) {
      return (
        <div className="row align-items-center mb-3">
          <div className="col-6">
            <label>¿Es Anticipo?</label>
          </div>
          <div className="col-6 text-end">
            <ReactSwitch checked={isAnticipo} onChange={setIsAnticipo} />
          </div>
        </div>
      );
    }
  };

  const renderMonto = () => {
    if (isNaN(parseInt(pago.idPago))) {
      return (
        <div className="input-group mb-3">
          <span className="input-group-text">$</span>
          <input
            step=".01"
            type="number"
            className="form-control border"
            value={getValue(pago, "monto")}
            onChange={(e) => setPropiedadPago("monto", e.target.value)}
          />
        </div>
      );
    }
    return (
      <p>
        {"$"}
        {formatMonto(pago.monto)}
      </p>
    );
  };

  const renderAutorizacion = () => {
    if (isNaN(parseInt(pago.idPago))) {
      return (
        <input
          type="date"
          className="form-control"
          value={getValue(pago, "fecha_hora_autorizacion", "date")}
          onChange={(e) =>
            setPropiedadPago("fecha_hora_autorizacion", e.target.value)
          }
        />
      );
    }
    return <p>{moment(pago.fecha_hora_autorizacion).format("DD MMM YYYY")}</p>;
  };

  const renderLiberacion = () => {
    if (!isNaN(parseInt(pago.idPago))) {
      return (
        <div>
          <label>Fecha Liberado</label>
          {pago.fecha_hora_liberado === null ? (
            <input
              type="date"
              className="form-control"
              value={getValue(pago, "fecha_hora_liberado", "date")}
              onChange={(e) =>
                setPropiedadPago("fecha_hora_liberado", e.target.value)
              }
            />
          ) : (
            <p>{moment(pago.fecha_hora_liberado).format("DD MMM YYYY")}</p>
          )}
        </div>
      );
    }
  };

  const renderEstimacion = () => {
    if (pago && pago !== null) {
      if (isNaN(parseInt(pago.idPago)) && !isAnticipo) {
        return (
          <select
            value={idEstimacion || ""}
            className="form-control mb-3"
            onChange={(e) =>
              setIdEstimacion(
                isNaN(parseInt(e.target.value)) ? null : e.target.value
              )
            }
          >
            <option value={null}>Seleccionar...</option>
            {estimaciones?.map((estimacion) => (
              <option
                key={estimacion.idEstimacion}
                value={estimacion.idEstimacion}
              >
                Estimación #{estimacion.numero} -{" "}
                {moment(estimacion.fecha_inicio).format("DD MMM YYYY")}
              </option>
            ))}
          </select>
        );
      }
      if (pago.Estimacion && pago.Estimacion !== null) {
        const estimacion = pago.Estimacion;
        return (
          <p>
            Estimación #{estimacion.numero} -{" "}
            {moment(estimacion.fecha_inicio).format("DD MMM YYYY")}
          </p>
        );
      }
    }
  };

  const renderAmortizacion = () => {
    if (!isAnticipo) {
      return (
        <div>
          <label>Monto Amortizado</label>
          <div className="input-group mb-3">
            <span className="input-group-text">$</span>
            <input
              step=".01"
              type="number"
              className={`form-control border ${
                errorAmortizado ? "border-danger" : ""
              }`}
              value={getValue(pago, "monto_amortizado")}
              onChange={(e) =>
                setPropiedadPago("monto_amortizado", e.target.value)
              }
            />
          </div>
          {errorAmortizado && (
            <span className="small text-danger">
              El monto máximo para amortizar es de ${formatMonto(porAmortizar)}
            </span>
          )}
        </div>
      );
    }
  };

  if (!pago || pago === null) return <div className="spinner-border" />;

  return (
    <form onSubmit={handleSubmit} className="px-0">
      {renderAnticipo()}
      {!isAnticipo && <label>Estimación</label>}
      {renderEstimacion()}
      {sugerencia}
      <label>Monto</label>
      {renderMonto()}
      {renderAmortizacion()}
      <div className="row mb-3">
        <div className="col-12 col-md-6">
          <label>Fecha Autorizado</label>
          {renderAutorizacion()}
        </div>
        <div className="col-12 col-md-6">{renderLiberacion()}</div>
      </div>
      <label>Concepto</label>
      <input
        type="text"
        className="form-control mb-3"
        value={getValue(pago, "comentario")}
        onChange={(e) => setPropiedadPago("comentario", e.target.value)}
      />
      <label>Comprobante</label>
      <input
        type="file"
        className="form-control"
        disabled={!["admin", "finanzas"].includes(permiso)}
      />
      <div className="row align-items-center mt-3">
        <div className="col-6">
          <button
            type="button"
            onClick={handleCancel}
            className="btn text-muted w-100"
          >
            Cancelar
          </button>
        </div>
        <div className="col-6">
          <button
            type="submit"
            disabled={errorAmortizado}
            className="btn btn-primary w-100"
          >
            Guardar
          </button>
        </div>
      </div>
    </form>
  );
};

export default PagoForm;
